<template>
  <div class="container-fluid">
    <b-form
      autocomplete="off"
      class="o-form"
      @submit="onSubmit"
      @reset="onReset"
    >
      <!------------------------------------ Save/Cancel --------------------------------------->
      <b-row class="pt-3 pb-3">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>
      <!-- --->
      <b-row>
        <b-col
          sm="12"
          lg="4"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.enabled"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Enabled
        </b-col>
        <b-col />
      </b-row>
      <b-row>
        <b-col
          cols="12"
          class="my-2"
        >
          <b-form-group
            class="o-field"
            :state="nameState"
          >
            <label for="name">Name</label>
            <b-form-input
              id="name"
              v-model="form.name"
              placeholder="Name"
              :state="nameState"
              trim
              @keyup="throttledMethod(validateUniqueName)"
            />
            <div class="invalid-tooltip">
              {{ nameInvalidFeedback }}
            </div>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="my-2"
        >
          <b-form-group class="o-field">
            <label for="displayName">Display Name</label>
            <b-form-input
              id="displayName"
              v-model="form.displayName"
              placeholder="Display Name"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="showConsentFields">
        <b-col
          cols="12"
          class="my-2 my-lg-0"
        >
          <b-form-group class="o-field">
            <label for="description">Description</label>
            <b-form-input
              id="description"
              v-model="form.description"
              placeholder="Description"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!------------------------------------ User Claims ---------------------------------------->
      <b-row>
        <b-col class="pb-2">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addUserClaim()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addUserClaim"
              v-b-toggle.collapse-userClaims
              :disabled="!hasUserClaims"
              variant="link"
              class="px-3 py-0 o-btn-link o-strong"
            >
              User Claims&nbsp;
              <i
                v-if="hasUserClaims"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasUserClaims"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>

          <b-collapse
            id="collapse-userClaims"
            v-model="collapseUserClaims"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(claim, claimIndex) in form.userClaims"
                :key="'claim' + claimIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'claim' + claimIndex"
                      class="text-truncate"
                    >Claim Type</label>
                    <b-form-input
                      :id="'claim' + claimIndex"
                      v-model="claim.type"
                      required
                      placeholder="Claim Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteUserClaim(claimIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Secrets ---------------------------------------->
      <b-row>
        <b-col class="pb-2">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addSecret()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addSecret"
              v-b-toggle.collapse-secrets
              :disabled="!hasSecrets"
              variant="link"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Secrets&nbsp;
              <i
                v-if="hasSecrets"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasSecrets"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>

          <b-collapse
            id="collapse-secrets"
            v-model="collapseSecrets"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(secret, secretIndex) in form.secrets"
                :key="'secret.id' + secretIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'secretVal' + secretIndex"
                      class="text-truncate"
                    >Hash</label>
                    <b-form-input
                      :id="'secretVal' + secretIndex"
                      v-model="secret.value"
                      required
                      placeholder="Hash"
                    />
                  </b-form-group>
                  <b-form-group>
                    <input
                      :id="'secretType' + secretIndex"
                      name="secret.type"
                      type="text"
                      value="SharedSecret"
                      hidden="hidden"
                    >
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteSecret(secretIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>
      <!------------------------------------ Scopes ---------------------------------------->
      <b-row>
        <b-col class="pb-2">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addScope()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addScope"
              v-b-toggle.collapse-scopes
              :disabled="!hasScopes"
              variant="link"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Scopes&nbsp;
              <i
                v-if="hasScopes"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasScopes"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-scopes"
            v-model="collapseScopes"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(scope, scopeIndex) in form.scopes"
                :key="'scope.id' + scopeIndex"
                class="my-2"
              >
                <b-col>
                  <b-row>
                    <b-col cols="11">
                      <b-form-group class="o-field">
                        <label
                          :for="'scopeName' + scopeIndex"
                          class="text-truncate"
                        >Name</label>
                        <b-form-input
                          :id="'scopeName' + scopeIndex"
                          v-model="scope.scope"
                          required
                          placeholder="Name"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      cols="1"
                      class="my-1 my-lg-0"
                    >
                      <br style="clear: both">
                      <b-button
                        v-b-tooltip.hover
                        class="btn-sm o-btn-trash"
                        title="Delete Row"
                        @click="deleteScope(scopeIndex)"
                      >
                        <i class="fa fa-trash" />
                      </b-button>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-alert
            v-model="showAlert"
            variant="danger"
            dismissible
          >
            {{ alertMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <!------------------------------------ Save/Cancel --------------------------------------->
      <b-row class="pt-3 pb-3 justify-content-around">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
export default {
  data() {
    return {
      isNameUnique: true,
      showConsentFields: false,
      collapseUserClaims: false,
      collapseSecrets: false,
      collapseScopes: false,
      form: {
        id: 0,
        name: "",
        displayName: "",
        description: "",
        enabled: true,
        userClaims: [],
        secrets: [],
        scopes: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      isLoading: "isLoading",
    }),
    showAlert: {
      get() {
        return this.$store.getters.showAlert;
      },
      set(value) {
        this.$store.dispatch("showAlert", value);
      },
    },
    alertMessage: {
      get() {
        return this.$store.getters.alertMessage;
      },
      set(value) {
        this.$store.dispatch("alertMessage", value);
      },
    },
    hasUserClaims: function () {
      return this.form.userClaims.length > 0;
    },
    hasSecrets: function () {
      return this.form.secrets.length > 0;
    },
    hasScopes: function () {
      return this.form.scopes.length > 0;
    },
    // Validation - Name Field
    nameState() {
      return this.form.name.length >= 4 && this.isNameUnique;
    },
    nameInvalidFeedback() {
      if (this.form.name.length < 4) {
        return "Enter at least 4 characters";
      } else if (!this.isNameUnique) {
        return "Must be unique across all Tenants";
      }
      return null;
    },
  },
  created() {
    this.loadApiResource();
  },
  methods: {
    deleteUserClaim(claimIndex) {
      this.form.userClaims.splice(claimIndex, 1);
      this.collapseUserClaims = this.hasUserClaims;
    },
    addUserClaim() {
      if (this.form.userClaims === undefined) {
        this.form.userClaims = [];
      }
      this.form.userClaims.push({
        id: 0,
        type: "",
      });
      this.collapseUserClaims = this.hasUserClaims;
    },
    deleteSecret(secretIndex) {
      this.form.secrets.splice(secretIndex, 1);
      this.collapseSecrets = this.hasSecrets;
    },
    addSecret() {
      if (this.form.secrets === undefined) {
        this.form.secrets = [];
      }
      this.form.secrets.push({
        id: 0,
        value: "",
        type: "SharedSecret",
      });
      this.collapseSecrets = this.hasSecrets;
    },
    deleteScope(scopeIndex) {
      this.form.scopes.splice(scopeIndex, 1);
      this.collapseScopes = this.hasScopes;
    },
    addScope() {
      if (this.form.scopes === undefined) {
        this.form.scopes = [];
      }
      this.form.scopes.push({
        id: 0,
        scope: "",
      });
      this.collapseScopes = this.hasScopes;
    },
    throttledMethod: function (func) {
      let debounced = _.debounce(func.bind(this), 500);
      debounced();
    },
    // validation
    validateUniqueName() {
      let value = this.form.name;
      if (value.length >= 4) {
        this.$axios
          .get(`/api/apiresources/${this.form.id}/isUnique/name/${value}`)
          .then((response) => {
            this.validationError = response.data;
            this.isNameUnique = response.data.valid;
            console.log("Result = " + this.isNameUnique);
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    onSubmit(evt) {
      evt.preventDefault();
      if (this.$route.params.apiResourceId == undefined) {
        this.createApiResource();
      } else {
        this.updateApiResource();
      }
    },
    onReset(evt) {
      evt.preventDefault();
      this.$router.push({ name: "apiResources" });
    },
    loadApiResource() {
      if (this.$route.params.apiResourceId > 0) {
        this.$axios
          .get(`/api/apiResources/${this.$route.params.apiResourceId}`)
          .then((response) => {
            this.form = response.data;
            this.expandPanelsOnLoad();
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    createApiResource() {
      this.$axios
        .post("/api/apiResources/new", this.form)
        .then(() => {
          this.$router.push({ name: "apiResources" });
        })
        .catch((error) => {
          this.alertMessage = error;
        });
    },
    updateApiResource() {
      this.$axios
        .put(
          `/api/apiResources/${this.$route.params.apiResourceId}/edit`,
          this.form
        )
        .then(() => {
          this.$router.push({ name: "apiResources" });
        })
        .catch((error) => {
          console.log("ERROR: " + JSON.stringify(error));
          this.alertMessage = error.errors[0];
        });
    },
    expandPanelsOnLoad() {
      this.collapseUserClaims = this.hasUserClaims;
      this.collapseSecrets = this.hasSecrets;
      this.collapseScopes = this.hasScopes;
    },
  },
};
</script>
